/**
 * Photos on a job (Leads and jobs, migration 0088): the limits, a resize that keeps phone photos small, and plain-words messages for what
 * the job-photos Edge Function can answer. A copy of the limits in packages/shared/jobs/photos.ts (the web app does not import shared code).
 */

export const PHOTO_MAX_BYTES = 8_000_000;
/** Photos are scaled down to this on their longest side before sending (a phone photo is often 4000 px and 5 MB or more). */
export const PHOTO_MAX_SIDE = 1600;
export const PHOTO_ACCEPT = "image/jpeg,image/png,image/webp,image/heic,image/heif";

const RESIZABLE = ["image/jpeg", "image/png", "image/webp"];

/**
 * Shrinks a photo to PHOTO_MAX_SIDE and re-saves it as a JPEG, so uploads are quick on a phone connection. A photo the browser cannot decode
 * (some HEIC files) or that would not get smaller is sent as it is.
 */
export async function shrinkPhoto(file: File): Promise<File> {
  if (!RESIZABLE.includes(file.type) || typeof createImageBitmap !== "function" || typeof document === "undefined") return file;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, PHOTO_MAX_SIDE / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close?.();
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.82));
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], file.name.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" });
  } catch {
    return file;
  }
}

/** What the person sees for each answer the Edge Function gives. */
export function photoErrorMessage(code: string): string {
  switch (code) {
    case "link_expired":
      return "This link has expired. Ask the business to send you a new one.";
    case "link_full":
      return "The most photos this link allows have been sent. Thank you.";
    case "invalid_link":
      return "This link is not valid. Check you opened the whole link from the text message.";
    case "too_big":
      return "That photo is too big. Try a smaller one.";
    case "not_an_image":
      return "That file is not a photo we can use. Send a JPEG, PNG or HEIC photo.";
    case "empty":
      return "That file is empty.";
    case "not_authorized":
      return "You do not have permission to change photos here.";
    case "network":
      return "Could not connect. Check your signal and try again.";
    default:
      return "That could not be sent. Please try again.";
  }
}
